import { ActivatedRoute, Router } from '@angular/router';
import {
  Component,
  ComponentFactoryResolver,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';

import { fromJS } from 'immutable';
import { get } from 'lodash';
import { Subscription, Unsubscribable } from 'rxjs';
import * as moment from 'moment';

import { ValuesExchangeProvider, LayoutProviderService } from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';

import { IWizardEntry } from './wizard.entry';
import { NewSiteWizardComponent } from './flows/sites/new.site.wizard';
import { UserUpdateWizardComponent } from './flows/user/update.user/user.update.wizard';
import { UserAddWizardComponent } from './flows/user/add.user.to.group/user.add.wizard.component';
import { UserRemoveWizardComponent } from './flows/user/remove.user.from.group/user.remove.wizard.component';
import { UpdateUserPasswordWizardComponent } from './flows/user/update.user.password/update.user.password';
import { NewUserGroupWizardComponent } from './flows/user/new.user.group/new.user.group.wizard';
import { AddUserWizardComponent } from './flows/user/add.user/add.user.wizard.component';
import { NewApplicationWizardComponent } from './flows/application/new.application.wizard';
import { AddUserToRolesWizard } from './flows/user/add.users.to.roles/add.user.to.roles.wizard';
import { RemoveUserFromRolesWizard } from './flows/user/remove.users.from.roles/remove.user.from.roles.wizard';
import { DatasourceWizardComponent } from './flows/datasource/datasource.wizard';
import { UserEmailNotificationWizard } from './flows/user/user.email.notification/user.email.notification';
import { UserSiteSelectionWizard } from './flows/user/user.sites.selection.component/user.site.selection.wizard';
import { IPAMConfigurationWizardComponent } from './flows/ipam/ipam.configuration.wizard';
import { SyslogConfigurationWizardComponent } from './flows/syslog/syslog.configuration.wizard';
import { ActiveTestWizardComponent } from './flows/active.test/active.test.wizard.component';
import { MlSelectApplicationWizardComponent } from './flows/machine.learning/ml-select-application-wizard/ml-select-application-wizard.component';
import { MlSelectSiteWizardComponent } from './flows/machine.learning/ml-select-site-wizard/ml-select-site-wizard.component';
import { MlSelectKpiWizardComponent } from './flows/machine.learning/ml-select-kpi-wizard/ml-select-kpi-wizard.component';
import { ThresholdWizardComponent } from './flows/threshold/threshold.wizard';
import { FusionSsoUserRemoveWizardComponent } from './flows/user/fusion.remove.user.from.group/fusion.user.remove.wizard.component';
import { FusionSsoUserAddWizardComponent } from './flows/user/fusion.sso.add.user.to.group/fusion.sso.user.add.wizard.component';
import { LdapServerWizardComponent } from './flows/ldap-servers/ldap.server.wizard.component';
import { RadiusServerWizardComponent } from './flows/radius-servers/radius.server.wizard.component';

@Component({
  selector: 'wizard-route-starter',
  templateUrl: 'wizard.router.starter.template.html'
})
export class WizardRouteStarterComponent implements OnInit, OnDestroy {
  @ViewChild('placeholder', { read: ViewContainerRef, static: true })
  viewContainerRef: ViewContainerRef;
  wizardId: string;
  wizardConfig: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  dataId: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  component: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  interestSubscr: Unsubscribable;
  routeSubscription: Subscription;

  constructor(
    private readonly exchange: ValuesExchangeProvider,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly layoutProviderService: LayoutProviderService,
    private readonly factoryResolver: ComponentFactoryResolver,
    private readonly navigator: NavigationProvider
  ) {}

  ngOnInit() {
    this.exchange.notifyListeners('route_guard', { noValue: true });
    this.interestSubscr = this.exchange.registerInterest(
      'route_guard',
      this.routeGuardListener.bind(this)
    );
    this.routeSubscription = this.activatedRoute.params.subscribe(params => {
      this.wizardId = params.wizardId;
      this.dataId = this.activatedRoute.snapshot.queryParamMap.get('dataId');
      this.layoutProviderService.getLayout().then(layout => {
        const activeLayout = fromJS(layout);
        const allWizards = activeLayout.get('registry').get('wizard').toJS();
        this.wizardConfig = allWizards[this.wizardId];
        this.startWizard();
      });
    });
  }

  ngOnDestroy() {
    this.interestSubscr.unsubscribe();
    this.routeSubscription.unsubscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  routeGuardListener(event: any): void {
    if (get(event, 'wizardId') === this.wizardId) {
      return;
    }
    if (!get(event, 'noValue')) {
      this.component.instance.wizardService.toggleClose(() => {
        this.navigate(event);
        this.exchange.notifyListeners('on_close', event);
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  navigate(event: any): void {
    this.navigator.navigatePerspective(event.id).navigateToWizard([event.wizardId]);
  }

  startWizard(): void {
    const cType = this.resolveWizardEntry();
    const factory = this.factoryResolver.resolveComponentFactory(cType);
    this.viewContainerRef.clear();
    this.component = this.viewContainerRef.createComponent(factory);
    (this.component.instance as IWizardEntry).wizardConfig = this.wizardConfig.wizardConfig;
    (this.component.instance as IWizardEntry).wizardId = this.wizardId;
    (this.component.instance as IWizardEntry).dataId = { id: this.dataId };
    (this.component.instance as IWizardEntry).onClose.subscribe(this.onCloseListener.bind(this));
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveWizardEntry(): Type<any> {
    switch (this.wizardId) {
      case 'new_application':
      case 'update_application':
        return NewApplicationWizardComponent;
      case 'add_sites':
      case 'update_sites':
        return NewSiteWizardComponent;
      case 'update_user':
      case 'update_fusion_sso_user':
        return UserUpdateWizardComponent;
      case 'add_user_to_groups':
        return UserAddWizardComponent;
      case 'remove_user_from_group':
        return UserRemoveWizardComponent;
      case 'update_user_password':
      case 'update_user_password_admin':
        return UpdateUserPasswordWizardComponent;
      case 'new_user_group':
        return NewUserGroupWizardComponent;
      case 'add_users_to_roles':
        return AddUserToRolesWizard;
      case 'remove_users_from_roles':
        return RemoveUserFromRolesWizard;
      case 'add_new_user':
      case 'add_new_user_fusion':
      case 'add_new_user_fusion_sso':
        return AddUserWizardComponent;
      case 'ml_select_application_for_reset':
        return MlSelectApplicationWizardComponent;
      case 'ml_select_site_for_reset':
        return MlSelectSiteWizardComponent;
      case 'ml_select_kpi_for_reset':
        return MlSelectKpiWizardComponent;
      case 'add_datasource':
      case 'update_datasource':
      case 'update_gigatest_datasource':
      case 'upgrade_agent':
      case 'add_agent':
        return DatasourceWizardComponent;
      case 'add_site_threshold':
      case 'add_app_threshold':
      case 'add_appsite_threshold':
      case 'update_threshold':
      case 'add_hist_server_threshold':
      case 'add_hist_site_threshold':
      case 'add_hist_app_threshold':
      case 'add_hist_appsite_threshold':
      case 'add_hist_appserver_threshold':
      case 'add_hist_siteserver_threshold':
      case 'add_hist_appsiteserver_threshold':
      case 'update_historical_threshold':
        return ThresholdWizardComponent;
      case 'user_app_selection':
        return UserEmailNotificationWizard;
      case 'user_site_selection':
        return UserSiteSelectionWizard;
      case 'ipam_config':
        return IPAMConfigurationWizardComponent;
      case 'syslog_config':
      case 'update_syslog_config':
        return SyslogConfigurationWizardComponent;
      case 'active_test':
        return ActiveTestWizardComponent;
      case 'fusion_sso_remove_user_from_group':
        return FusionSsoUserRemoveWizardComponent;
      case 'fusion_sso_add_user_to_groups':
        return FusionSsoUserAddWizardComponent;
      case 'add_ldap_server':
      case 'update_ldap_server':
        return LdapServerWizardComponent;
      case 'add_radius_server':
      case 'update_radius_server':
        return RadiusServerWizardComponent;
      default: {
        console.error('Unrecognized wizardId:', this.wizardId);
        return null;
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onCloseListener(value: any): void {
    // please note: the navigation below is supposed to 'close' wizard outlet. the url to be shown
    // will be based on 'activatedRoute.parent' - ie, original page. However, if your original page included
    // query parameters, the navigation from below will discard them.
    // in case you need to preserve query parameters, consider either moving them to path parameter (eg /profile/:userName),
    // or change the relativeTo section into something like this:
    // {relativeTo: this.activatedRoute.parent, queryParamsHandling: 'preserve'}
    // however, this will be not enough to preserve query params - you will need to find places, where 'wizard' outlet is being
    // opened, and add queryParamsHandling: 'preserve'. For example, ptc-dashboard.component::navigateToWizard
    const timestamp = moment().unix();
    this.exchange.notifyListeners('on_close', { value, timestamp });
    this.navigator.navigateToWizard(null, { relativeTo: this.activatedRoute.parent });
  }
}
